import React from "react";
import { Space } from "antd";
import {
  AxDatePicker,
  AxDateRangePicker,
  AxDivider,
  AxButton,
} from "@dynata/axiom3";

class Datepicker extends React.Component {
  render() {
    return (
      <div className="section" id="datepicker">
        <h3>Date Picker</h3>
        <Space direction="vertical" size="middle">
          <p>To select or input a date.</p>

          <Space size="large" direction="vertical">
            <div className="prop-header">
              <p className="props-title">Props</p>
              <p>
                For AntD props, visit{" "}
                <AxButton
                  size="small"
                  type="link"
                  target="_blank"
                  href="https://ant.design/components/date-picker/#API"
                >
                  https://ant.design/components/date-picker/#API
                </AxButton>
              </p>
            </div>

            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>size</td>
                  <td>Size of the Date Picker</td>
                  <td>small | medium | large</td>
                  <td>
                    <code>medium</code>
                  </td>
                </tr>

                <tr>
                  <td>allowClear</td>
                  <td>Whether to show clear button. Default is, true</td>
                  <td>boolean</td>
                  <td></td>
                </tr>

                <tr>
                  <td>disabled</td>
                  <td>
                    Determine whether the DatePicker is disabled. Default is,
                    false
                  </td>
                  <td>boolean</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>status</td>
                  <td>Sets warning or error states.</td>
                  <td>warning | error</td>
                  <td>-</td>
                </tr>
                <tr>
                  <td>defaultPickerValue</td>
                  <td>To set default picker date</td>
                  <td>
                    <a href="http://momentjs.com/">moment</a>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>defaultValue</td>
                  <td>
                    To set default date, if start time or end time is null or
                    undefined, the date range will be an open interval
                  </td>
                  <td>
                    <a href="http://momentjs.com/">moment</a>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>disabledTime</td>
                  <td>To specify the time that cannot be selected</td>
                  <td>function(date)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>format</td>
                  <td>
                    To set the date format, refer to{" "}
                    <a href="http://momentjs.com/">moment.js</a>. When an array
                    is provided, all values are used for parsing and first value
                    is used for formatting, support{" "}
                    <a href="#components-date-picker-demo-format">
                      Custom Format
                    </a>
                  </td>
                  <td>
                    string | (value: moment) =&gt; string | (string | (value:
                    moment) =&gt; string)[]
                  </td>
                  <td>
                    <code>YYYY-MM-DD</code>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>renderExtraFooter</td>
                  <td>Render extra footer in panel</td>
                  <td>(mode) =&gt; React.ReactNode</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>showNow</td>
                  <td>
                    Whether to show 'Now' button on panel when{" "}
                    <code>showTime</code> is set
                  </td>
                  <td>boolean</td>
                  <td>-</td>
                  <td>4.4.0</td>
                </tr>
                <tr>
                  <td>showTime</td>
                  <td>To provide an additional time selection</td>
                  <td>object | boolean</td>
                  <td>
                    <a href="/components/time-picker/#API">
                      TimePicker Options
                    </a>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>showTime.defaultValue</td>
                  <td>
                    To set default time of selected date,{" "}
                    <a href="#components-date-picker-demo-disabled-date">
                      demo
                    </a>
                  </td>
                  <td>
                    <a href="http://momentjs.com/">moment</a>
                  </td>
                  <td>moment()</td>
                  <td></td>
                </tr>
                <tr>
                  <td>showToday</td>
                  <td>
                    Whether to show <code>Today</code> button
                  </td>
                  <td>boolean</td>
                  <td>true</td>
                  <td></td>
                </tr>
                <tr>
                  <td>value</td>
                  <td>To set date</td>
                  <td>
                    <a href="http://momentjs.com/">moment</a>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onChange</td>
                  <td>
                    Callback function, can be executed when the selected time is
                    changing
                  </td>
                  <td>function(date: moment, dateString: string)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onOk</td>
                  <td>Callback when click ok button</td>
                  <td>function()</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onPanelChange</td>
                  <td>Callback function for panel changing</td>
                  <td>function(value, mode)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>...others</td>
                  <td>
                    All extra props are passed down to the{" "}
                    <>
                      <AxButton
                        size="small"
                        type="link"
                        target="_blank"
                        href="https://4x.ant.design/components/date-picker/#API"
                      >
                        {`<DatePicker />`}
                      </AxButton>
                    </>{" "}
                    component.
                  </td>
                  <td></td>
                  <td>-</td>
                  <td></td>
                </tr>
              </tbody>
            </table>

            <pre>{`
<AxDatePicker size="small" />
<AxDatePicker />
<AxDatePicker disabled />
<AxDatePicker size="large" />`}</pre>
            <AxDatePicker size="small" />
            <AxDatePicker />
            <AxDatePicker disabled />
            <AxDatePicker size="large" allowClear />
          </Space>

          <AxDivider orientation="left" plain>
            Range Picker
          </AxDivider>

          <Space size="large" direction="vertical">
            <pre>{`
<AxDateRangePicker size="small" />
<AxDateRangePicker />
<AxDateRangePicker disabled />
<AxDateRangePicker size="large" />`}</pre>
            <AxDateRangePicker size="small" />
            <AxDateRangePicker />
            <AxDateRangePicker disabled />
            <AxDateRangePicker size="large" allowClear />
          </Space>
        </Space>
      </div>
    );
  }
}
export default Datepicker;
